<template>
  <Teleport to="body">
    <!-- visibility:hidden, not v-show — OSMD reads container.offsetWidth, which
         display:none zeroes, giving a degenerate score and broken measure bounds. -->
    <div
      class="fixed inset-0 z-50 flex flex-col bg-background"
      :class="{ invisible: !open }"
    >
      <div class="relative flex items-center gap-3 px-4 py-3 border-b border-surface-hover bg-secondary shrink-0">
        <UTabs
          v-if="!loopViewOnly"
          v-model="activeTab"
          :items="tabsItems"
          :content="false"
          class="shrink-0"
        />

        <h2 class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-base font-semibold">Loop Section</h2>

        <button
          class="ml-auto p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
          aria-label="Close"
          @click="$emit('close')"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <div
        v-if="loopViewOnly"
        class="flex items-center gap-2 px-4 py-2 border-b border-surface-hover bg-surface/40 text-xs text-text-secondary shrink-0"
        role="note"
      >
        <InformationCircleIcon class="w-4 h-4 shrink-0" />
        <span>You cannot toggle loops as a participant.</span>
      </div>

      <div class="flex-1 relative overflow-hidden">
        <SheetMusicSectionsEditor
          v-model:sections="displaySections"
          :music-xml-file="musicXmlFile"
          :selected-id="editorSelectedId"
          :editable="activeTab === 'custom'"
          :read-only="loopViewOnly"
          :active="open"
          @section-edit-start="handleSectionEditStart"
          @section-edit-end="handleSectionEditEnd"
          @section-click="handleSectionClick"
        >
          <template #section-label="{ section }">
            <template v-if="activeTab === 'piece'">
              <span>{{ section.name }}</span>
              <span
                v-if="section.id === activeSectionId && gameEngine.loopSource.value === 'piece'"
                class="ml-1 text-[10px] opacity-80"
              >(looping)</span>
            </template>
            <template v-else>
              <span>My Loop</span>
              <span
                v-if="gameEngine.loopSource.value === 'custom'"
                class="ml-1 text-[10px] opacity-80"
              >(looping)</span>
            </template>
          </template>

          <template v-if="activeTab === 'custom'" #footer>
            <div class="shrink-0 px-4 py-3 border-t border-surface-hover bg-secondary flex items-center gap-3">
              <template v-if="localCustomSection">
                <div class="flex-1 text-sm text-text-secondary">
                  Measures {{ localCustomSection.startMeasureIndex + 1 }}–{{ localCustomSection.endMeasureIndex + 1 }}
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm">Enable loop</span>
                  <USwitch
                    :model-value="gameEngine.loopSource.value === 'custom'"
                    @update:model-value="setCustomLoopEnabled"
                    :ui="{ base: 'data-[state=checked]:bg-text-primary' }"
                  />
                </div>
                <button
                  class="text-xs text-error hover:underline"
                  @click="deleteCustomSection"
                >
                  Delete
                </button>
              </template>
              <template v-else>
                <p class="text-sm text-text-secondary flex-1">
                  Drag across measures to create your loop section.
                </p>
              </template>
            </div>
          </template>
        </SheetMusicSectionsEditor>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
import type { PieceSection } from '@/types';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useUserLoopSectionStorage } from '@/composables/useUserLoopSectionStorage';
import SheetMusicSectionsEditor from './SheetMusicSectionsEditor.vue';

const styles = getComputedStyle(document.documentElement);
const LOOP_ACTIVE_COLOR = styles.getPropertyValue('--color-loop-active').trim();
const LOOP_INACTIVE_COLOR = styles.getPropertyValue('--color-loop-inactive').trim();

const props = withDefaults(defineProps<{
  musicXmlFile: File;
  adminSections?: readonly PieceSection[];
  musicalPieceId?: string | null;
  loopViewOnly?: boolean;
  // Parent-owned visibility. Mounted while false so the score is pre-rendered.
  open?: boolean;
}>(), {
  loopViewOnly: false,
  open: false,
});

const emit = defineEmits<{
  'apply': [data: { startMeasure: number | null; endMeasure: number | null; source: 'piece' | 'custom' | undefined }];
  'close': [];
}>();
const gameEngine = useInjectGameEngine();
const { userLoopSection, saveUserLoopSection, clearUserLoopSection } = useUserLoopSectionStorage(
  computed(() => props.musicalPieceId)
);

const hasPieceSections = computed(() => !!props.adminSections?.length);
const activeTab = ref<'piece' | 'custom'>(
  props.loopViewOnly ? 'piece' : 'custom'
);

const tabsItems = computed(() => {
  const items = [];
  if (!props.loopViewOnly) {
    items.push({ label: 'My Loop', value: 'custom' });
  }
  if (hasPieceSections.value) {
    items.push({ label: 'Piece Sections', value: 'piece' });
  }
  return items;
});

const activeSectionId = ref<string | null>(null);
const isCustomEditing = ref(false);
const hasPendingCustomEmit = ref(false);

const localCustomSection = ref<{ id: string; startMeasureIndex: number; endMeasureIndex: number } | null>(
  userLoopSection.value
    ? {
        id: '__custom__',
        startMeasureIndex: userLoopSection.value.startMeasureIndex,
        endMeasureIndex: userLoopSection.value.endMeasureIndex,
      }
    : null
);

const toCustomSection = (startMeasureIndex: number, endMeasureIndex: number) => ({
  id: '__custom__',
  startMeasureIndex,
  endMeasureIndex,
});

const syncStateFromProps = () => {
  const hasCurrentLoopRange = gameEngine.loopStartMeasure.value != null && gameEngine.loopEndMeasure.value != null;
  const matchingSection = hasCurrentLoopRange
    ? props.adminSections?.find(
        (s) => s.startMeasureIndex === gameEngine.loopStartMeasure.value && s.endMeasureIndex === gameEngine.loopEndMeasure.value
      )
    : null;

  if (props.loopViewOnly) {
    activeSectionId.value =
      gameEngine.loopSource.value === 'piece' && matchingSection ? matchingSection.id : null;
    return;
  }

  const persistedCustom = userLoopSection.value
    ? toCustomSection(userLoopSection.value.startMeasureIndex, userLoopSection.value.endMeasureIndex)
    : null;

  if (!isCustomEditing.value) {
    if (gameEngine.loopSource.value === 'custom' && hasCurrentLoopRange) {
      localCustomSection.value = toCustomSection(gameEngine.loopStartMeasure.value!, gameEngine.loopEndMeasure.value!);
    } else {
      localCustomSection.value = persistedCustom;
    }
  } else if (!localCustomSection.value && persistedCustom) {
    localCustomSection.value = persistedCustom;
  }

  if (hasCurrentLoopRange && gameEngine.loopSource.value === undefined) {
    gameEngine.loopSource.value = matchingSection ? 'piece' : 'custom';
  }

  if (!hasPieceSections.value) {
    activeTab.value = 'custom';
  } else if (gameEngine.loopSource.value !== undefined && hasCurrentLoopRange) {
    if (gameEngine.loopSource.value === 'custom') {
      activeTab.value = 'custom';
    } else if (gameEngine.loopSource.value === 'piece' && matchingSection) {
      activeTab.value = 'piece';
    } else {
      activeTab.value = matchingSection ? 'piece' : 'custom';
    }
  }

  if (gameEngine.loopSource.value === undefined || !hasCurrentLoopRange) {
    activeSectionId.value = null;
    return;
  }

  if (gameEngine.loopSource.value === 'custom') {
    activeSectionId.value = null;
    return;
  }

  if (gameEngine.loopSource.value === 'piece' && matchingSection) {
    activeSectionId.value = matchingSection.id;
    return;
  }

  if (matchingSection) {
    activeSectionId.value = matchingSection.id;
    return;
  }

  activeSectionId.value = null;
};

watch(
  [
    userLoopSection,
    () => gameEngine.loopSource.value,
    () => gameEngine.loopStartMeasure.value,
    () => gameEngine.loopEndMeasure.value,
    () => props.adminSections,
  ],
  syncStateFromProps,
  { immediate: true }
);

const editorSelectedId = computed(() => {
  if (activeTab.value === 'piece') return activeSectionId.value;
  return localCustomSection.value?.id ?? null;
});

const displaySections = ref<PieceSection[]>([]);

watch([activeTab, () => props.adminSections, localCustomSection, () => gameEngine.loopSource.value], () => {
  if (activeTab.value === 'piece') {
    displaySections.value = [...(props.adminSections ?? [])];
    return;
  }

  if (!localCustomSection.value) {
    displaySections.value = [];
    return;
  }

  displaySections.value = [{
    id: localCustomSection.value.id,
    name: 'My Loop',
    color: gameEngine.loopSource.value === 'custom' ? LOOP_ACTIVE_COLOR : LOOP_INACTIVE_COLOR,
    startMeasureIndex: localCustomSection.value.startMeasureIndex,
    endMeasureIndex: localCustomSection.value.endMeasureIndex,
  }];
}, { immediate: true });

watch(activeTab, (tab) => {
  if (tab === 'custom') return;
  isCustomEditing.value = false;
  hasPendingCustomEmit.value = false;
});

watch(displaySections, (newSections) => {
  if (activeTab.value !== 'custom' || newSections.length === 0) return;

  const latest = newSections[newSections.length - 1];
  if (newSections.length > 1) {
    displaySections.value = [latest];
    return;
  }

  if (
    localCustomSection.value?.startMeasureIndex === latest.startMeasureIndex &&
    localCustomSection.value?.endMeasureIndex === latest.endMeasureIndex
  ) {
    return;
  }

  localCustomSection.value = {
    id: latest.id,
    startMeasureIndex: latest.startMeasureIndex,
    endMeasureIndex: latest.endMeasureIndex,
  };
  gameEngine.loopSource.value = 'custom';
  if (isCustomEditing.value) {
    hasPendingCustomEmit.value = true;
    return;
  }
  emitCurrentSelection();
}, { deep: true });

function handleSectionClick(id: string) {
  if (activeTab.value === 'piece') {
    if (activeSectionId.value === id && gameEngine.loopSource.value === 'piece') {
      activeSectionId.value = null;
      disableLoop();
      return;
    }

    activeSectionId.value = id;
    enableLoop('piece');
    return;
  }

  if (localCustomSection.value?.id !== id) return;
  setCustomLoopEnabled(gameEngine.loopSource.value !== 'custom');
}

function deleteCustomSection() {
  localCustomSection.value = null;
  clearUserLoopSection();
  emitCurrentSelection();
}

function enableLoop(source: 'piece' | 'custom') {
  gameEngine.loopSource.value = source;
  emitCurrentSelection();
}

// Switching a loop off parks its range in the engine so the toolbar toggle resumes this
// same section; discarding it — a delete, or a selection that no longer resolves — drops
// that memory.
function disableLoop() {
  gameEngine.setLoopEnabled(false);
  emitLoopCleared();
}

// Shared by the footer switch and tapping the section itself.
function setCustomLoopEnabled(enabled: boolean) {
  if (enabled) enableLoop('custom');
  else disableLoop();
}

function clearAppliedLoop() {
  gameEngine.clearLoopSection();
  emitLoopCleared();
}

function emitLoopCleared() {
  emit('apply', { startMeasure: null, endMeasure: null, source: undefined });
}

function emitCurrentSelection() {
  if (activeTab.value === 'piece') {
    const section = props.adminSections?.find((s) => s.id === activeSectionId.value);
    if (!section || gameEngine.loopSource.value !== 'piece') {
      clearAppliedLoop();
      return;
    }

    gameEngine.setLoopSection(section.startMeasureIndex, section.endMeasureIndex, true, 'piece');
    emit('apply', {
      startMeasure: section.startMeasureIndex,
      endMeasure: section.endMeasureIndex,
      source: 'piece',
    });
    return;
  }

  if (!localCustomSection.value || gameEngine.loopSource.value !== 'custom') {
    clearAppliedLoop();
    return;
  }

  const { startMeasureIndex, endMeasureIndex } = localCustomSection.value;
  gameEngine.setLoopSection(startMeasureIndex, endMeasureIndex, true, 'custom');
  saveUserLoopSection({
    startMeasureIndex,
    endMeasureIndex,
  });
  emit('apply', {
    startMeasure: startMeasureIndex,
    endMeasure: endMeasureIndex,
    source: 'custom',
  });
}

function handleSectionEditStart() {
  if (activeTab.value !== 'custom') return;
  isCustomEditing.value = true;
}

function handleSectionEditEnd() {
  if (activeTab.value !== 'custom') return;
  isCustomEditing.value = false;
  if (!hasPendingCustomEmit.value) return;
  hasPendingCustomEmit.value = false;
  enableLoop('custom');
}

</script>
